import React, { useMemo } from "react";
import { Tooltip } from "@material-ui/core";
import { getActionCardColors } from "./ActionCardColors";
import { useMobileDetection } from "../../utils/deviceDetection";
import ActionCardHeader from "./ActionCardHeader";
import ActionCardBody from "./ActionCardBody";
import PlayerBadge from "../ui/PlayerBadge";

/**
 * AppliedActionCard - Displays a full action card that has been applied to a stock
 * Positioned underneath the stock card with header peeking out above
 * Non-interactive (no click handlers, no hover effects)
 */
const AppliedActionCard = ({ 
  actionCard, 
  styleCardSize, 
  offset, 
  zIndex,
  playerColors = {},
  playerEmojis = {}
}) => {
  const colors = useMemo(() => getActionCardColors(actionCard), [actionCard]);
  const isMobile = useMobileDetection();
  
  const cardHeight = styleCardSize?.height || '210px';
  const cardWidth = styleCardSize?.width || '150px';

  const cardStyle = useMemo(() => ({
    width: cardWidth,
    height: cardHeight,
    background: colors.bg,
    borderColor: colors.border,
    borderWidth: '3px',
    // borderStyle comes from CSS .stock-card class (solid)
    // Don't set borderRadius - let CSS .stock-card class handle it (16px)
    // Don't set padding - let CSS .stock-card class handle it (8px)
    position: 'absolute',
    top: `${offset}px`,
    // Don't set right - use width instead to match stock card exactly
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    zIndex: zIndex,
    pointerEvents: 'none',
    overflow: 'visible', // Allow header to peek out above stock card
    // box-sizing, padding, border-radius, display, flexDirection all come from .stock-card CSS class
  }), [cardWidth, cardHeight, colors.bg, colors.border, offset, zIndex]);

  const headerStyle = useMemo(() => ({
    position: 'relative',
    zIndex: zIndex + 10,
    pointerEvents: 'auto'
  }), [zIndex]);

  // Build tooltip content with description and player name (with avatar and color)
  const tooltipContent = useMemo(() => {
    if (!actionCard.description && !actionCard.playerName) {
      return null;
    }
    
    return (
      <div className="whitespace-pre-line text-left">
        {actionCard.description && (
          <div className={actionCard.playerName ? 'mb-sm' : ''}>
            {actionCard.description}
          </div>
        )}
        {actionCard.playerName && (
          <div className={actionCard.description ? 'mt-sm pt-sm border-t border-light' : ''}>
            <PlayerBadge
              playerName={actionCard.playerName}
              playerColor={playerColors[actionCard.playerName]}
              playerEmoji={playerEmojis[actionCard.playerName]}
              labelPrefix="Played by:"
              size="medium"
            />
          </div>
        )}
      </div>
    );
  }, [actionCard.description, actionCard.playerName, playerColors, playerEmojis]);

  return (
    <div
      className="stock-card card"
      style={cardStyle}
    >
      <div style={headerStyle}>
        {tooltipContent ? (
          <Tooltip title={tooltipContent} arrow placement="top">
            <div style={{ cursor: 'help' }}>
              <ActionCardHeader 
                action={actionCard} 
                colors={colors} 
                isMobile={isMobile}
                hideDelayedEmoji={actionCard.actionType === 'delayed_manipulate' && actionCard.emojiRemoved}
                cardWidth={cardWidth}
              />
            </div>
          </Tooltip>
        ) : (
          <ActionCardHeader 
            action={actionCard} 
            colors={colors} 
            isMobile={isMobile}
            hideDelayedEmoji={actionCard.actionType === 'delayed_manipulate' && actionCard.emojiRemoved}
            cardWidth={cardWidth}
          />
        )}
      </div>
      <ActionCardBody 
        action={actionCard} 
        colors={colors} 
        isMobile={isMobile} 
      />
    </div>
  );
};

export default AppliedActionCard;

